import Swal from "sweetalert2";
import {
  build_campaign_form_data,
  get_category_name,
  get_campaign_form_values,
  get_empty_campaign_form,
  update_campaign_in_lists,
} from "../DonationCampaigns/donation_campaigns_form_helper";
import {
  get_campaign_cover_image,
  get_campaign_file_icon_class,
  get_campaign_file_name,
  get_campaign_file_url,
  format_datetime,
  get_campaign_file_created_at,
  get_campaign_supporting_files_count,
  get_progress,
  format_currency,
  format_date,
  is_campaign_image_file,
  is_campaign_video_file,
} from "../DonationCampaigns/donation_campaigns_methods_helper";

function get_url_api() {
  return window?.URL || window?.webkitURL || null;
}

function get_local_preview_file_url(file) {
  const url_api = get_url_api();
  return !file || !url_api?.createObjectURL ? "" : url_api.createObjectURL(file);
}

function revoke_local_preview_file_url(url) {
  const url_api = get_url_api();
  if (url && url_api?.revokeObjectURL) {
    url_api.revokeObjectURL(url);
  }
}

function is_local_pdf_file(file) {
  return String(file?.type || "").toLowerCase() === "application/pdf"
    || String(file?.name || "").toLowerCase().includes(".pdf");
}

function get_local_preview_file_data(file) {
  return ({
    name: file?.name || "Selected file",
    url: get_local_preview_file_url(file),
    is_image: String(file?.type || "").startsWith("image/"),
    is_video: String(file?.type || "").startsWith("video/"),
    is_pdf: is_local_pdf_file(file),
    is_local: true,
  });
}


export const my_donation_campaigns_methods = {
  reset_form() {
    this.campaign_form = {
      ...get_empty_campaign_form(),
      cover_image: null,
      cover_image_preview: "",
    };
    this.editing_campaign_id = null;
    this.campaign_document_files = [];
    this.clear_form_errors();
  },
  fetch_my_campaigns_api() {
    if (!this.token_exists) {
      this.my_campaigns_data = [];
      return Promise.resolve();
    }

    this.is_loading_my_campaigns = true;
    return $.ajax({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/donation_management/get_my_campaigns/`,
      method: "GET",
      headers: this.get_request_headers(),
      success: (data) => {
        this.my_campaigns_data = data?.data || [];
      },
      complete: () => {
        this.is_loading_my_campaigns = false;
      },
    });
  },
  fetch_campaign_categories_api() {
    this.is_loading_categories = true;
    return $.ajax({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/donation_management/get_campaign_categories/`,
      method: "GET",
      headers: this.get_request_headers(),
      success: (data) => {
        this.categories = data?.data || [];
      },
      complete: () => {
        this.is_loading_categories = false;
      },
    });
  },
  get_category_name(category_id) {
    return get_category_name(this.categories, category_id, "");
  },
  get_campaign_file_icon_class,
  get_campaign_file_name,
  get_campaign_file_url,
  format_datetime,
  get_campaign_file_created_at,
  is_campaign_image_file,
  is_campaign_video_file,
  get_local_file_preview_url(file) {
    return get_local_preview_file_url(file);
  },
  open_existing_file_preview(file) {
    this.preview_file = {
      name: this.get_campaign_file_name(file),
      url: this.get_campaign_file_url(file),
      is_image: is_campaign_image_file(file),
      is_video: is_campaign_video_file(file),
      is_pdf: String(this.get_campaign_file_url(file) || this.get_campaign_file_name(file) || "").toLowerCase().includes(".pdf"),
    };
    $("#campaign_supporting_file_preview_modal").modal("show");
  },
  open_local_file_preview(file) {
    this.preview_file = get_local_preview_file_data(file);
    $("#campaign_supporting_file_preview_modal").modal("show");
  },
  clear_file_preview() {
    if (this.preview_file?.is_local && this.preview_file?.url) {
      revoke_local_preview_file_url(this.preview_file.url);
    }
    this.preview_file = null;
  },
  populate_form_from_campaign(campaign) {
    if (!campaign) return;
    this.campaign_form = {
      ...get_campaign_form_values(campaign),
      cover_image: null,
      cover_image_preview: get_campaign_cover_image(campaign),
    };
    this.campaign_document_files = [];
  },
  trigger_campaign_cover_upload() {
    if (!this.token_exists || this.is_uploading_campaign_image) {
      return;
    }
    this.$refs.campaign_cover_input?.click();
  },
  handle_campaign_cover_change(event) {
    const file = event?.target?.files?.[0];
    if (!file) return;
    this.campaign_form.cover_image = file;
    this.campaign_form.cover_image_preview = this.get_local_file_preview_url(file);
    event.target.value = "";
  },
  handle_campaign_document_change(event) {
    const files = Array.from(event?.target?.files || []);
    if (!files.length) return;
    const existing_keys = new Set(
      this.campaign_document_files.map((file) => `${file.name}-${file.size}-${file.lastModified}`)
    );
    const next_files = files.filter((file) => {
      const key = `${file.name}-${file.size}-${file.lastModified}`;
      if (existing_keys.has(key)) {
        return false;
      }
      existing_keys.add(key);
      return true;
    });

    this.campaign_document_files = [...this.campaign_document_files, ...next_files];
    event.target.value = "";
  },
  remove_pending_campaign_document(index) {
    this.campaign_document_files.splice(index, 1);
  },
  build_campaign_form_data() {
    return build_campaign_form_data(this.campaign_form, this.campaign_document_files);
  },
  build_supporting_documents_form_data(campaign_id) {
    const form_data = new FormData();
    form_data.append("campaign_id", campaign_id);
    this.campaign_document_files.forEach((file) => {
      form_data.append("files", file);
    });
    return form_data;
  },
  has_campaign_title_for_file_upload() {
    return String(this.campaign_form.title || "").trim();
  },
  set_supporting_file_title_error() {
    this.errors.title = "Campaign title is required before uploading supporting files.";
  },
  sync_campaign_lists(saved_campaign) {
    const updated_lists = update_campaign_in_lists({
      updated_campaign: saved_campaign,
      my_campaigns_data: this.my_campaigns_data,
      public_campaigns: this.public_campaigns,
    });
    this.my_campaigns_data = updated_lists.my_campaigns_data;
    this.public_campaigns = updated_lists.public_campaigns;
    this.selected_campaign_id = updated_lists.selected_campaign_id;
  },
  calculate_days_left(end_date) {
    if (!end_date) return 0;
    const today = new Date();
    const end = new Date(end_date);
    today.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    const diff = Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  },
  start_edit_campaign(campaign) {
    if (this.token_exists && !this.categories.length) {
      this.fetch_campaign_categories_api();
    }
    this.editing_campaign_id = campaign.id;
    this.selected_campaign_id = campaign.id;
    this.populate_form_from_campaign(campaign);
    this.active_tab = "create";
    this.manage_campaign_id = null;
  },
  back_to_manage_campaigns() {
    this.active_tab = "manage";
    this.reset_form();
    this.campaign_document_files = [];
  },
  toggle_campaign_status(campaign) {
    if (!campaign?.id || !this.token_exists) return;

    const next_status = campaign.status === "active" ? "paused" : "active";
    this.is_toggling_campaign_id = campaign.id;

    const form_data = new FormData();
    form_data.append("campaign_id", campaign.id);
    form_data.append("status", next_status);

    $.ajax({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/donation_management/change_campaign_status/`,
      method: "POST",
      headers: this.get_request_headers(),
      data: form_data,
      processData: false,
      contentType: false,
      success: (response_data) => {
        const updated_campaign = response_data?.data || {};
        const updated_lists = update_campaign_in_lists({
          updated_campaign,
          my_campaigns_data: this.my_campaigns_data,
          public_campaigns: this.public_campaigns,
        });
        this.my_campaigns_data = updated_lists.my_campaigns_data;
        this.selected_campaign_id = updated_lists.selected_campaign_id;
        Swal.fire({
          icon: "success",
          title: next_status === "active" ? "Campaign published." : "Campaign paused.",
          showConfirmButton: false,
          timer: 2200,
          position: "bottom-end",
          toast: true
        });
      },
      error: (error) => {
        Swal.fire({
          icon: "error",
          title: this.get_api_error_message(error, "Unable to update campaign status."),
          showConfirmButton: false,
          timer: 2500,
          position: "bottom-end",
          toast: true
        });
      },
      complete: () => {
        this.is_toggling_campaign_id = null;
      },
    });
  },
  open_campaign_detail(campaign) {
    if (!campaign?.id) return;
    this.$router.push({ name: "donation_campaigns", query: { campaign_id: campaign.id } });
  },
  go_to_explore_page() {
    this.$router.push({ name: "donation_campaigns" });
  },
  get_manage_filter_count(filter) {
    return this.my_campaigns_data.filter((campaign) => {
      return filter === "all" ? true : campaign.status === filter;
    }).length;
  },
  get_campaign_cover_image,
  get_campaign_supporting_files_count,
  get_progress,
  format_currency,
  format_date,
};
